import type { ReactNode } from 'react';
import { filterItems } from '@forecastify/shared';
import {
  Button,
  Icon,
  MultiSelect,
  Select,
  TextInput,
  type IconName,
  type SelectOption,
} from '@forecastify/ui';
import { useT } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C67 · One settings page: title row, toolbar, body. The mockup's measures. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **ELEVEN SCREENS WERE ELEVEN LAYOUTS, AND THAT IS WHAT *"DIFFERENT SIZES, DIFFERENT
 * BUTTONS, TABLES NOT UNIFIED"* MEANT.** Each section drew its own header strip, its own search
 * box at its own width, its own card padding and its own primary button. The mockup has exactly
 * one of each, so this does too, and a section now supplies content rather than chrome.
 *
 * The measures are the mockup's, not chosen here:
 *
 * | part | `Forecastify - workspace beallitasok (standalone).html` |
 * | --- | --- |
 * | title row | `min-h-[56px] pt-4 pb-2 px-4`, title `text-[17px] font-semibold` |
 * | toolbar | `gap-2 px-4 py-3`, search `w-[236px]`, each filter `w-[164px]` |
 * | body | `min-h-0 flex-1 overflow-auto px-4 pt-1 pb-6` |
 *
 * ⚠️⚠️ **THE BODY SCROLLS, NOT THE CARD.** *"Edges of the table cut off, can't see the border all
 * around."* Putting `overflow-x-auto` inside the card runs its rows under its own right edge; the
 * padding here plus a `min-width` on the card means a wide table scrolls the page and the border
 * stays whole on all four sides.
 */
export function SettingsPage({
  title,
  action,
  secondaryAction,
  search,
  onSearch,
  searchPlaceholder,
  filters = [],
  tabs,
  error,
  children,
  /**
   * ⚠️⚠️ **THREE WIDTHS, AND THE MOCKUP HAS ALL THREE.** `headCls` in
   * `Forecastify - workspace beallitasok (standalone).html`: a form centres on 680, an INDEX — a
   * list of things to drill into, like the metafield entities or the permission levels — centres
   * on 760, and a collection table spans the section.
   */
  width = 'full',
}: {
  readonly title: string;
  /** ⚠️ `disabled` is for a form page's Save — nothing to save, or a value the server would refuse. */
  readonly action?: { label: string; icon?: IconName; disabled?: boolean; onSelect: () => void };
  readonly secondaryAction?: {
    label: string;
    icon?: IconName;
    disabled?: boolean;
    onSelect: () => void;
  };
  readonly search?: string;
  readonly onSearch?: (value: string) => void;
  readonly searchPlaceholder?: string;
  readonly filters?: {
    key: string;
    value: string;
    ariaLabel: string;
    options: readonly SelectOption[];
    onChange: (value: string) => void;
    /**
     * 3 Nov 2026 · given, the filter picks SEVERAL (`MultiSelect`, with *Összes kijelölése* /
     * *Kijelölés törlése*) and this is what it says with nothing chosen. `value` is then the
     * chosen values joined with a comma, `''` for none — the list toolbar's own shape.
     */
    multiPlaceholder?: string;
  }[];
  readonly tabs?: ReactNode;
  readonly error?: string | null;
  readonly children: ReactNode;
  readonly width?: 'full' | 'form' | 'index';
}) {
  const t = useT();
  const centred = width !== 'full';
  const frame =
    width === 'form'
      ? 'mx-auto w-full max-w-[680px] px-5'
      : width === 'index'
        ? 'mx-auto w-full max-w-[760px] px-5'
        : 'px-4';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className={centred ? 'shrink-0 pr-[10px]' : 'shrink-0'}>
        <div className={`flex min-h-[56px] flex-wrap items-center gap-2 pt-4 pb-2 ${frame}`}>
          {/* ⚠️⚠️ **THE SECTION TITLE IS BACK — asked for by name.** *"You can even add the extra
              title to make it more unified."* Every settings page now names itself in the same
              place, at the same size, with its primary action on the same line. */}
          <h1 className="truncate text-[17px] font-semibold">{title}</h1>
          <div className="min-w-0 flex-1" />
          {secondaryAction === undefined ? null : (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={secondaryAction.disabled}
              onClick={secondaryAction.onSelect}
              data-tour="settings-secondary"
            >
              {secondaryAction.icon === undefined ? null : (
                <Icon name={secondaryAction.icon} size={13} />
              )}
              {secondaryAction.label}
            </Button>
          )}
          {action === undefined ? null : (
            <Button
              size="sm"
              className="gap-1.5"
              disabled={action.disabled}
              onClick={action.onSelect}
              data-tour="settings-primary"
            >
              {action.icon === undefined ? null : <Icon name={action.icon} size={13} />}
              {action.label}
            </Button>
          )}
        </div>
      </div>

      {tabs === undefined ? null : <div className="shrink-0 px-4">{tabs}</div>}

      {error === null || error === undefined ? null : (
        <p role="alert" className="text-destructive shrink-0 px-4 pb-2 text-[12px]">
          {error}
        </p>
      )}

      {onSearch === undefined && filters.length === 0 ? null : (
        <div className="flex shrink-0 flex-wrap items-center gap-2 px-4 py-3">
          {onSearch === undefined ? null : (
            /* ⚠️ The magnifier sits INSIDE the field at `left-2.5`, with `pl-8` to clear it — the
               mockup's own arrangement, and the reason the box is 236px rather than a round
               number: it is sized for the placeholder it carries. */
            <div className="relative w-[236px] shrink-0">
              <Icon
                name="search"
                size={14}
                className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2"
              />
              <TextInput
                value={search ?? ''}
                placeholder={searchPlaceholder ?? t('list.search')}
                aria-label={searchPlaceholder ?? t('list.search')}
                className="w-full pl-8"
                onChange={(event) => {
                  onSearch(event.target.value);
                }}
              />
            </div>
          )}
          {filters.map((filter) => (
            <span key={filter.key} className="w-[164px] shrink-0">
              {filter.multiPlaceholder === undefined ? (
                <Select
                  value={filter.value}
                  aria-label={filter.ariaLabel}
                  options={filter.options}
                  className="w-full"
                  onChange={(event) => {
                    filter.onChange(event.target.value);
                  }}
                />
              ) : (
                <MultiSelect
                  label={filter.ariaLabel}
                  placeholder={filter.multiPlaceholder}
                  options={filter.options}
                  values={filterItems(filter.value)}
                  onValuesChange={(values) => {
                    filter.onChange(values.join(','));
                  }}
                  selectAllLabel={t('list.selectAll')}
                  deselectAllLabel={t('list.clearSelection')}
                />
              )}
            </span>
          ))}
        </div>
      )}

      <div className={`min-h-0 flex-1 overflow-auto pt-1 pb-6 ${frame}`}>{children}</div>
    </div>
  );
}
